import React, { useState, useCallback } from "react";
import * as Icon from 'react-feather';
import MerchandiseSelector from '../components/MerchandisesSelector';
import PortSelector from '../components/PortSelector';
import axios from '../util/Api';
import { InputNumber, Spin, message, Modal } from 'antd';
import { tokensKey } from '../util/Constants';
import Gallery from "react-photo-gallery";
import Carousel, { ModalGateway } from "react-images";
import { saveAs } from 'file-saver';
import JSZip from "jszip";
import BillsDivider from "./BillsDivider.js";
import getStorage from '../context/auth';

function PhotosComponent({ photos }) {
  const [currentImage, setCurrentImage] = useState(0);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);

  const openLightbox = useCallback((event, { index }) => {
    setCurrentImage(index);
    setViewerIsOpen(true);
  }, []);

  const closeLightbox = () => {
    setCurrentImage(0);
    setViewerIsOpen(false);
  };

  return (
    <div>
      <Gallery photos={photos} onClick={openLightbox} />
      <ModalGateway>
        {viewerIsOpen && (
          <Modal onClose={closeLightbox}>
            <Carousel
              currentIndex={currentImage}
              views={photos.map(x => ({
                ...x,
                srcset: x.srcSet,
                caption: x.title
              }))}
            />
          </Modal>
        )}
      </ModalGateway>
    </div>
  );
}

class MeasureDetails extends React.Component {
  state = {
    isLoading: false,
    isDeletingDividedBons: false,
    selectedElement: null,
    dataToUpdate: [],
    portsToUpdate: null,
    annexedUsers: [],
    usersData: [],
    fetchingUsers: false,
    onEditBills: false,
    canDivideBons: false,
    bonsFullyDivided: false,
    dividedBons: [],
    activeBonDecoupeId: null,
    canDeleteDividedBons: false,
    customerDeleteAlreadyUsed: false,
  };

  componentDidMount() {
    this.checkProps();
  }

  componentDidUpdate(prevProps) {
    if (this.props.selectedElement !== prevProps.selectedElement) {
      this.checkProps();
    }
  }

  checkProps = () => {
    if (this.props.selectedElement !== this.state.selectedElement?.id) {
      this.setState({
        selectedElementId: this.props.selectedElement,
        canDivideBons: false,
        bonsFullyDivided: false,
        activeBonDecoupeId: null,
        canDeleteDividedBons: false,
        customerDeleteAlreadyUsed: false,
      }, this.getMeasureElement);
    }
  };

  getMeasureElement = () => {
    this.setState({ isLoading: true });
    const { jwt, user } = JSON.parse(getStorage().getItem(tokensKey)) || {};
    if (!jwt) return;

    axios.get(`api/mesures/${this.props.selectedElement}/full-details`, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then((result) => {
        if (result.status === 200) {
          const selectedElement = result.data.data?.attributes || result.data;
          // attach a stable ui id to each line for smooth editing
          const merchWithUid = (selectedElement.Merchandises || []).map((m, i) => {
            const initialWeight = (m?.weight !== undefined && m?.weight !== null)
              ? Number(m.weight)
              : ((m?.announcedWeight !== undefined && m?.announcedWeight !== null) ? Number(m.announcedWeight) : undefined);
            return {
              _uid: m.id || `${Date.now()}-${i}`,
              ...m,
              weight: initialWeight,
            };
          });
          this.setState({
            selectedElement,
            dataToUpdate: merchWithUid,
            portsToUpdate: selectedElement.PortFiller || { origin_port: null, arrival_port: null },
            annexedUsers: selectedElement.user_annexes || [],
            isLoading: false
          });

          if (user?.canDivideBons) {
            this.getDividingElements(selectedElement.id);
          } else {
            this.setState({
              canDivideBons: false,
              bonsFullyDivided: false,
              activeBonDecoupeId: null,
              canDeleteDividedBons: false,
              customerDeleteAlreadyUsed: false,
            });
          }
        }
      })
      .catch((e) => {
        console.error("Error fetching measure element:", e);
        this.setState({ isLoading: false });
      });
  };

  getDividingElements = (measureId) => {
    const { jwt } = JSON.parse(getStorage().getItem(tokensKey)) || {};
    axios.get(`api/bons-decoupes/by-measure/${measureId}`, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(result => {
        if (result.status === 200) {
          const bonsData = result.data.data || [];
          const activeBonDecoupe = bonsData[0] || null;
          const bons = activeBonDecoupe?.bons || [];
          const deleteCount = Number(activeBonDecoupe?.customer_delete_count || 0);
          this.setState({
            canDivideBons: !activeBonDecoupe,
            bonsFullyDivided: !!activeBonDecoupe,
            dividedBons: bons.map(bon => ({
              ...bon,
              url: bon.bon?.url || ''
            })),
            activeBonDecoupeId: activeBonDecoupe?.id || null,
            canDeleteDividedBons: !!activeBonDecoupe && deleteCount < 1,
            customerDeleteAlreadyUsed: deleteCount >= 1,
          });
        }
      })
      .catch((e) => {
        this.setState({
          canDivideBons: false,
          bonsFullyDivided: false,
          dividedBons: [],
          activeBonDecoupeId: null,
          canDeleteDividedBons: false,
          customerDeleteAlreadyUsed: false,
        });
      });
  };

  confirmDeleteDividedBons = (e) => {
    e?.preventDefault?.();
    const { activeBonDecoupeId, isDeletingDividedBons } = this.state;

    if (!activeBonDecoupeId || isDeletingDividedBons) {
      return;
    }

    Modal.confirm({
      title: 'Supprimer ce découpage ?',
      content: "Le client pourra refaire le découpage, mais cette suppression n'est autorisée qu'une seule fois.",
      okText: 'Supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: this.deleteDividedBons,
    });
  };

  deleteDividedBons = () => {
    const { activeBonDecoupeId } = this.state;
    const { jwt } = JSON.parse(getStorage().getItem(tokensKey)) || {};

    if (!jwt || !activeBonDecoupeId) {
      return Promise.resolve();
    }

    this.setState({ isDeletingDividedBons: true });

    return axios
      .post(
        `api/bons-decoupes/${activeBonDecoupeId}/customer-delete`,
        {},
        { headers: { Authorization: `Bearer ${jwt}` } }
      )
      .then(() => {
        message.success({
          content: 'Découpage supprimé avec succès',
        });
        this.getMeasureElement();
        if (this.props.onLiteUpdated) this.props.onLiteUpdated();
      })
      .catch((err) => {
        const errorMessage =
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Une erreur est survenue';
        message.error({
          content: errorMessage,
        });
      })
      .finally(() => this.setState({ isDeletingDividedBons: false }));
  };

  updateMerchandise = () => {
    const { dataToUpdate, portsToUpdate, selectedElement } = this.state;
    const keys = dataToUpdate.map(e => e.marchandise?.id || e.marchandise?.name || '');
    const commentContent = document.getElementById("comments_content")?.value || '';

    if (!portsToUpdate?.arrival_port) {
      message.error('Il faut choisir le port de destination');
      return;
    }

    if ((new Set(keys)).size !== keys.length) {
      message.error('Vous ne pouvez pas avoir deux marchandises identiques');
      return;
    }

    // guard against empty / invalid lines
    const hasIncomplete = dataToUpdate.some((item) => !item?.marchandise || item.weight === undefined || item.weight === null);
    if (hasIncomplete) {
      message.error('Veuillez compléter toutes les lignes (marchandise et tonnage).');
      return;
    }

    const totalWeight = dataToUpdate.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
    const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    if (round2(totalWeight) !== round2(selectedElement.net_resulting_tonnage)) {
      message.error('La somme des marchandises doit être égale au tonnage résultant Net');
      return;
    }

    const { jwt } = JSON.parse(getStorage().getItem(tokensKey)) || {};
    if (!jwt || !selectedElement.bon) return;

    this.setState({ isLoading: true });

    // sanitize payload (strip ui-only keys and send ids, not nested objects)
    const sanitizedMerch = dataToUpdate.map((item) => ({
      id: item?.id || undefined, // component id if exists (to update instead of recreate)
      marchandise: item?.marchandise?.id ?? item?.marchandise ?? null,
      weight: Number(item?.weight) || 0,
      announcedWeight: item?.announcedWeight ?? null,
      number: item?.number ?? '',
    }));

    // send only port ids + keep component id & flag when present
    const sanitizedPorts = portsToUpdate ? {
      id: portsToUpdate?.id || undefined,
      isArrivalResponsible: portsToUpdate?.isArrivalResponsible ?? false,
      origin_port: portsToUpdate.origin_port?.id ?? portsToUpdate.origin_port ?? null,
      arrival_port: portsToUpdate.arrival_port?.id ?? portsToUpdate.arrival_port ?? null,
    } : null;

    const authHeader = { headers: { Authorization: `Bearer ${jwt}` } };

    // sequential updates to avoid racing if the first fails
    // business rule: if comment is empty, send a single space " "
    const commentToSend = (commentContent && commentContent.trim() !== '') ? commentContent : ' ';
    const currentComment = (selectedElement.bon?.client_comment ?? ''); // do NOT trim for exact match
    const doBonUpdate = commentToSend !== currentComment;
    axios
      .put(`api/mesures/update/${selectedElement.id}`, { data: { Merchandises: sanitizedMerch, PortFiller: sanitizedPorts } }, authHeader)
      .then(() => doBonUpdate
        ? axios.put(`/api/bons/custom-update/${selectedElement.bon.id}`, { data: { client_comment: commentToSend } }, authHeader)
        : Promise.resolve())
      .then(() => {
        message.success('Saisie des marchandises modifiée avec succès');
        this.props.onUpdated();
      })
      .catch((err) => {
        console.error('Update error:', err?.response?.data || err);
        message.error('Une erreur est survenue');
      })
      .finally(() => this.setState({ isLoading: false }));
  };

  // ----- UX helpers for a smoother editing -----
  addMerchandiseRow = () => {
    const { dataToUpdate } = this.state;
    const newRow = { _uid: `tmp-${Date.now()}`, marchandise: null, weight: undefined };
    this.setState({ dataToUpdate: [...dataToUpdate, newRow] });
  };

  removeMerchandiseRow = (row) => {
    const { dataToUpdate } = this.state;
    const updated = dataToUpdate.filter((m) => m._uid !== row._uid);
    this.setState({ dataToUpdate: updated });
  };

  fetchUsers = (e) => {
    e.preventDefault();
    const searchItem = document.getElementById("search_users")?.value;
    const { annexedUsers } = this.state;

    if (!searchItem) return;

    this.setState({ fetchingUsers: true });
    const { jwt } = JSON.parse(getStorage().getItem(tokensKey)) || {};
    axios.get(`/users?filters[name][$contains]=${searchItem}&pagination[limit]=10`, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(result => {
        if (result.status === 200) {
          const substractedUsers = result.data.data.filter(
            ({ id }) => !annexedUsers.some(user => user.id === id)
          );
          this.setState({ usersData: substractedUsers });
        }
      })
      .catch(console.error)
      .finally(() => this.setState({ fetchingUsers: false }));
  };

  updateAnnexedUsers = (updatedUsers) => {
    const { selectedElement } = this.state;
    const { jwt } = JSON.parse(getStorage().getItem(tokensKey)) || {};

    axios.put(`api/mesures/update/${selectedElement.id}`, {
      data: {
        user_annexes: updatedUsers.map(e => e.id)
      }
    }, {
      headers: { Authorization: `Bearer ${jwt}` }
    })
      .then(() => this.props.onLiteUpdated())
      .catch(() => {
        message.error('Une erreur est survenue');
        return false;
      });
  };

  removeUser = (user) => {
    const { annexedUsers } = this.state;
    const updatedUsers = annexedUsers.filter(e => e.id !== user.id);
    this.setState({ annexedUsers: updatedUsers }, () => {
      this.updateAnnexedUsers(updatedUsers);
    });
  };

  addUser = (user) => {
    const { annexedUsers } = this.state;
    const updatedUsers = [...annexedUsers, user];
    this.setState({ annexedUsers: updatedUsers }, () => {
      this.updateAnnexedUsers(updatedUsers);
    });
  };

  downloadDividedBills = () => {
    const { dividedBons } = this.state;
    const files = dividedBons.filter(bon => bon.bon?.url);
    const zip = new JSZip();
    let count = 0;

    files.forEach(file => {
      axios.get(file.bon.url, { responseType: "blob" })
        .then(response => {
          zip.file(`${file.reference}.pdf`, response.data, { binary: true });
          if (++count === files.length) {
            zip.generateAsync({ type: "blob" }).then(content => {
              saveAs(content, "bons_decoupes.zip");
            });
          }
        })
        .catch(console.error);
    });
  };

  // Robustly parse weight from formatted text (supports comma or dot and unit suffix)
  safeParseWeight = (val, prev) => {
    try {
      if (val === null || val === undefined) return prev;
      const raw = String(val);
      const cleaned = raw.replace(/[^0-9,\.\-]/g, '').replace(/,/g, '.');
      if (!cleaned || cleaned === '-' || cleaned === '.' || cleaned === '-.') return prev;
      const n = parseFloat(cleaned);
      if (Number.isNaN(n)) return prev;
      return Math.round(n * 100) / 100;
    } catch (_) {
      return prev;
    }
  };

  render() {
    const {
      isLoading,
      annexedUsers,
      fetchingUsers,
      usersData,
      selectedElement,
      onEditBills,
      canDivideBons,
      bonsFullyDivided,
      isDeletingDividedBons,
      activeBonDecoupeId,
      canDeleteDividedBons,
      customerDeleteAlreadyUsed,
      dataToUpdate,
      portsToUpdate
    } = this.state;

    


    if (!selectedElement || isLoading) {
      return (
        <div className="row justify-content-center">
          <Spin />
        </div>
      );
    }

    const { user } = JSON.parse(getStorage().getItem(tokensKey)) || {};
    const candidates = this.props.updatedUser?.users_to_share?.users || [];
    const shareCandidates = Array.isArray(candidates)
      ? candidates.filter(u => u.id !== user?.id && !annexedUsers.some(a => a.id === u.id))
      : [];
    const showShareSection = selectedElement.user?.id === user?.id &&
      (annexedUsers.length > 0 || shareCandidates.length > 0);
    const canUpdate = user?.id === selectedElement.user?.id &&
      selectedElement.bon &&
      !selectedElement.bon.customer_invoice;

    const getPhotoUrl = (photo) => {
      return photo?.formats?.medium?.url || photo?.url || "images/measure/default_boat.png";
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const s = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      return s.charAt(0).toUpperCase() + s.slice(1);
    };

    const formatTonnage = (v) => {
      const n = Number(v);
      if (v === 0 || (!Number.isNaN(n) && v !== null && v !== undefined && v !== '')) {
        return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} t`;
      }
      return 'N/A';
    };

    console.log('══════════════════════════════════════════════');
    console.log('🚀 Rendering MeasureDetails');
    console.log('⏳ isLoading:', isLoading);
    console.log('👥 annexedUsers:', annexedUsers);
    console.log('🔄 fetchingUsers:', fetchingUsers);
    console.log('📋 usersData:', usersData);
    console.log('🎯 selectedElement:', selectedElement);
    console.log('✏️ onEditBills:', onEditBills);
    console.log('➗ canDivideBons:', canDivideBons);
    console.log('✅ bonsFullyDivided:', bonsFullyDivided);
    console.log('🗂️ activeBonDecoupeId:', activeBonDecoupeId);
    console.log('🗑️ canDeleteDividedBons:', canDeleteDividedBons);
    console.log('♻️ customerDeleteAlreadyUsed:', customerDeleteAlreadyUsed);
    console.log('📝 dataToUpdate:', dataToUpdate);
    console.log('⚓ portsToUpdate:', portsToUpdate);
    console.log('👥 user:', user);
    console.log('✏️ canUpdate:', canUpdate);

    // live helpers for UX
    const net = Number(selectedElement.net_resulting_tonnage || 0);
    const sumWeights = (dataToUpdate || []).reduce((s, it) => s + (Number(it.weight) || 0), 0);
    const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    const remaining = round2(net - sumWeights);
    const hasDuplicates = (() => {
      const ids = (dataToUpdate || []).map(e => e.marchandise?.id || e.marchandise?.name || null).filter(Boolean);
      return new Set(ids).size !== ids.length;
    })();
    const hasIncompleteLine = (dataToUpdate || []).some((e) => !e?.marchandise || e.weight === undefined || e.weight === null);
    const actionColumnClassName = "col-12 col-md-6 col-xl-4 mb-2 d-flex";
    const actionCardStyle = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      minHeight: 74,
      padding: '12px 16px',
      borderRadius: 22,
      background: '#ffffff',
      border: '1px solid rgba(60, 72, 88, 0.12)',
      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
      gap: 12,
      textAlign: 'left',
    };
    const actionLeadingStyle = {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      minWidth: 0,
      flex: 1,
    };
    const actionTextStyle = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 2,
      minWidth: 0,
    };
    const actionTitleStyle = {
      fontSize: '1rem',
      fontWeight: 700,
      color: '#2f3b52',
      lineHeight: 1.1,
    };
    const actionHintStyle = {
      fontSize: '0.82rem',
      color: '#7b8798',
      lineHeight: 1.2,
    };
    const actionStatusStyle = (destructive = false) => ({
      width: 38,
      height: 38,
      borderRadius: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background: destructive ? 'rgba(232, 73, 95, 0.12)' : 'rgba(59, 91, 219, 0.12)',
      color: destructive ? '#e03f56' : '#3050d8',
    });
    const actionIconStyle = (destructive = false) => ({
      width: 30,
      height: 30,
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background: destructive ? 'rgba(232, 73, 95, 0.08)' : 'rgba(59, 91, 219, 0.08)',
      color: destructive ? '#e03f56' : '#3050d8',
    });
    const infoCardStyle = {
      ...actionCardStyle,
      minHeight: 'auto',
      background: '#f8fafc',
      boxShadow: 'none',
      border: '1px solid rgba(120, 130, 145, 0.18)',
    };
    const renderActionCard = ({
      title,
      hint,
      leading,
      trailing,
      href,
      onClick,
      destructive = false,
      isButton = false,
    }) => {
      const card = (
        <div style={actionCardStyle}>
          <div style={actionLeadingStyle}>
            <div style={actionStatusStyle(destructive)}>
              {leading}
            </div>
            <div style={actionTextStyle}>
              <span style={actionTitleStyle}>{title}</span>
              <span style={actionHintStyle}>{hint}</span>
            </div>
          </div>
          <div style={actionIconStyle(destructive)}>
            {trailing}
          </div>
        </div>
      );

      if (isButton) {
        return (
          <button
            type="button"
            className="btn btn-link p-0 w-100"
            onClick={onClick}
            style={{ textDecoration: 'none' }}
          >
            {card}
          </button>
        );
      }

      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="w-100"
          style={{ textDecoration: 'none' }}
        >
          {card}
        </a>
      );
    };

        return (
      <div className="container measure-details-container">
        <div className="row justify-content-center">
          <div className="col-12 text-center">
            <div className="mja-date-header mb-4">
              <div className="mja-date-header__eyebrow">
                <Icon.Calendar size={15} />
                <span>Date de mesure</span>
              </div>
              <h4 className="mja-date-header__title mb-0">
                {formatDate(selectedElement.date)}
              </h4>
            </div>
          </div>

          <div className="col-12 col-xl-10 mb-1">
            <div className="row justify-content-center">
              {selectedElement.bon && (selectedElement.bon.customer_invoice || selectedElement.bon.controller_invoice) && (
                <div className={actionColumnClassName}>
                  {renderActionCard({
                    href: (selectedElement.bon.customer_invoice?.url) ?? selectedElement.bon.controller_invoice?.url,
                    title: 'Bon de relevé',
                    hint: 'Bon principal',
                    leading: <Icon.FileText size={18} />,
                    trailing: <Icon.Download size={16} />,
                  })}
                </div>
              )}

              {bonsFullyDivided && (
                <div className={actionColumnClassName}>
                  {renderActionCard({
                    title: 'Bons découpés',
                    hint: 'Archive ZIP',
                    leading: <Icon.Layers size={18} />,
                    trailing: <Icon.Download size={16} />,
                    onClick: this.downloadDividedBills,
                    isButton: true,
                  })}
                </div>
              )}

              {bonsFullyDivided && canDeleteDividedBons && user?.id === selectedElement.user?.id && (
                <div className={actionColumnClassName}>
                  {renderActionCard({
                    title: 'Réinitialiser',
                    hint: 'Découpage client • 1 fois',
                    leading: isDeletingDividedBons ? <Spin size="small" /> : <Icon.RotateCcw size={18} />,
                    trailing: <Icon.Trash2 size={16} />,
                    onClick: this.confirmDeleteDividedBons,
                    isButton: true,
                    destructive: true,
                  })}
                </div>
              )}

              {bonsFullyDivided && customerDeleteAlreadyUsed && user?.id === selectedElement.user?.id && (
                <div className="col-12 col-xl-8 mb-3">
                  <div style={infoCardStyle}>
                    <div style={actionLeadingStyle}>
                      <div style={actionStatusStyle(true)}>
                        <Icon.CheckCircle size={18} />
                      </div>
                      <div style={actionTextStyle}>
                        <span style={actionTitleStyle}>Suppression déjà utilisée</span>
                        <span style={actionHintStyle}>La réinitialisation client a déjà été consommée.</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {canDivideBons && user?.id === selectedElement.user?.id && (
                <div className={actionColumnClassName}>
                  {renderActionCard({
                    title: 'Découper',
                    hint: 'Créer des bons',
                    leading: <Icon.Edit3 size={18} />,
                    trailing: <Icon.ChevronRight size={16} />,
                    onClick: () => this.setState({ onEditBills: true }),
                    isButton: true,
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="row align-items-center">
            <div className="col-lg-6 mt-4 pt-2">
              <div className="card work-details rounded bg-light border-0">
                <div className="card-body">
                  <div className="mja-info-header">
                    <div className="mja-info-header__eyebrow">Bateau</div>
                    <h5 className="card-title mb-1">Informations bateau</h5>
                    <p className="mja-info-header__hint mb-0">Identité et caractéristiques principales</p>
                  </div>
                  <dl className="row mb-0">
                    <dt className="col-md-4 col-5">Nom :</dt>
                    <dd className="col-md-8 col-7 text-muted">{selectedElement.boat?.name || 'N/A'}</dd>
                    <dt className="col-md-4 col-5">Matricule :</dt>
                    <dd className="col-md-8 col-7 text-muted">{selectedElement.boat?.immatriculation || 'N/A'}</dd>
                    <dt className="col-md-4 col-5">Longueur :</dt>
                    <dd className="col-md-8 col-7 text-muted">{selectedElement.boat?.boat_length || 'N/A'}</dd>
                    <dt className="col-md-4 col-5">Largeur :</dt>
                    <dd className="col-md-8 col-7 text-muted">{selectedElement.boat?.boat_width || 'N/A'}</dd>
                    <dt className="col-md-4 col-5">Poids total :</dt>
                    <dd className="col-md-8 col-7 text-muted">{selectedElement.boat?.boat_total_weight || 'N/A'}</dd>
                    <dt className="col-md-4 col-5">Plat-bord :</dt>
                    <dd className="col-md-8 col-7 text-muted">{selectedElement.boat?.height || 'N/A'}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="col-lg-6 mt-4 pt-2">
              <img
                src={getPhotoUrl(selectedElement.boat?.default_photo)}
                className="img-fluid rounded boat-image"
                alt="Boat"
              />
            </div>
          </div>

          <div className="col-lg-11 col-md-11 mt-4">
            <div className="table-responsive">
              <table className="table table-center table-padding mb-0 measure-details-table">
                <tbody>
                  {portsToUpdate && (
                    <tr className="mja-route-row">
                      <td className="font-weight-bold border-0 mja-route-cell">
                        <div className="mja-route-card">
                          <span className="mja-section-pill">Chargement</span>
                          <div className="mja-route-value">
                            {portsToUpdate.origin_port?.name || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="font-weight-bold border-0 mja-route-cell mja-route-cell--right">
                        <div className="mja-route-card mja-route-card--right">
                          <span className="mja-section-pill">Déchargement</span>
                          {canUpdate ? (
                            <div className="mja-route-select">
                              <PortSelector
                                defaultValue={portsToUpdate.arrival_port}
                                className="mja-select--port"
                                dropdownClassName="mja-select-dropdown--port"
                                placeholder="Sélectionner un port"
                                onSelected={(port) => this.setState({
                                  portsToUpdate: { ...portsToUpdate, arrival_port: port }
                                })}
                              />
                            </div>
                          ) : (
                            <div className="mja-route-value">
                              {portsToUpdate.arrival_port?.name || 'N/A'}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}

                  {selectedElement.bon && (
                    <tr className="mja-detail-row">
                      <td className="mja-row-label">Numéro bon</td>
                      <td className="mja-row-value">{selectedElement.bon.unique_number || 'N/A'}</td>
                    </tr>
                  )}

                  <tr className="mja-detail-row">
                    <td className="mja-row-label">Tonnage annoncé</td>
                    <td className="mja-row-value">{formatTonnage(selectedElement.announced_tonnage)}</td>
                  </tr>

                  {canUpdate ? (
                    dataToUpdate.map((element, index) => (
                      <tr key={element._uid || index} className="mja-edit-row">
                        <td className="mja-edit-cell mja-edit-cell--merchandise">
                          <MerchandiseSelector
                            defaultValue={element.marchandise}
                            className="mja-select--merchandise"
                            dropdownClassName="mja-select-dropdown--merchandise"
                            placeholder="Choisir une marchandise"
                            onSelected={(merchandise) => {
                              const newData = [...dataToUpdate];
                              newData[index].marchandise = merchandise;
                              this.setState({ dataToUpdate: newData });
                            }}
                          />
                        </td>
                        <td className="mja-edit-cell mja-edit-cell--weight">
                          <div className="mja-weight-field">
                            <InputNumber
                              max={selectedElement.net_resulting_tonnage}
                              step={0.1}
                              precision={2}
                              value={(element.weight !== undefined && element.weight !== null)
                                ? Number(element.weight)
                                : (element.announcedWeight !== undefined && element.announcedWeight !== null
                                  ? Number(element.announcedWeight)
                                  : undefined)}
                              className="mja-input-number"
                              size="small"
                              inputMode="decimal"
                              onChange={(weight) => {
                                const newData = [...dataToUpdate];
                                newData[index].weight = this.safeParseWeight(weight, newData[index].weight);
                                this.setState({ dataToUpdate: newData });
                              }}
                              onBlur={(e) => {
                                const newData = [...dataToUpdate];
                                newData[index].weight = this.safeParseWeight(e?.target?.value, newData[index].weight);
                                this.setState({ dataToUpdate: newData });
                              }}
                              onPressEnter={(e) => {
                                const newData = [...dataToUpdate];
                                newData[index].weight = this.safeParseWeight(e?.target?.value, newData[index].weight);
                                this.setState({ dataToUpdate: newData });
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-link mja-delete p-0"
                              title="Supprimer"
                              onClick={() => this.removeMerchandiseRow(element)}
                            >
                              <Icon.Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    selectedElement.Merchandises?.map((element, index) => (
                      <tr key={index} className="mja-detail-row">
                        <td className="mja-row-label mja-row-label--value">{element.marchandise?.name || 'N/A'}</td>
                        <td className="mja-row-value">{formatTonnage(element.weight)}</td>
                      </tr>
                    ))
                  )}

                  {canUpdate && (
                    <tr>
                      <td colSpan={2} className="border-0 pt-3 mja-add-row">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success mja-add-btn"
                          onClick={this.addMerchandiseRow}
                        >
                          <Icon.Plus size={16} className="mr-1" /> Ajouter une marchandise
                        </button>
                      </td>
                    </tr>
                  )}

                  <tr className="mja-total-row">
                    <td className="h5 font-weight-bold">Tonnage jaugé</td>
                    <td className="text-center text-primary h4 font-weight-bold">
                      {formatTonnage(selectedElement.net_resulting_tonnage)}
                    </td>
                  </tr>
                </tbody>
              </table>

              {canUpdate && (
                <div className="mt-3 mja-progress-summary">
                  <div className="mja-progress-summary__header">
                    <div className="mja-progress-summary__metrics">
                      <span className="mja-progress-chip mja-progress-chip--filled">
                        Somme {round2(sumWeights)} t
                      </span>
                      <span className={`mja-progress-chip ${remaining === 0 ? 'mja-progress-chip--success' : remaining < 0 ? 'mja-progress-chip--danger' : 'mja-progress-chip--neutral'}`}>
                        Reste {remaining} t
                      </span>
                    </div>
                    <small className="text-muted mja-progress-summary__hint">
                      Mention obligatoire: tous les champs doivent être complétés pour générer le document.
                    </small>
                  </div>
                  <div className="mja-progress-summary__flags">
                    {hasDuplicates && (
                      <small className="mja-progress-flag mja-progress-flag--danger">Doublons de marchandise détectés</small>
                    )}
                    {hasIncompleteLine && (
                      <small className="mja-progress-flag mja-progress-flag--warning">Lignes incomplètes</small>
                    )}
                  </div>
                  <div className="progress mja-progress-bar-wrap" style={{ height: 8 }}>
                    <div
                      className={`progress-bar ${remaining === 0 ? 'bg-success' : remaining < 0 ? 'bg-danger' : 'bg-primary'}`}
                      role="progressbar"
                      style={{ width: `${Math.min(100, Math.max(0, (sumWeights / (net || 1)) * 100))}%` }}
                      aria-valuenow={sumWeights}
                      aria-valuemin={0}
                      aria-valuemax={net}
                    />
                  </div>
                </div>
              )}

              {showShareSection && (
                <div className="mja-share-section">
                  <div className="mja-share-header">
                    <div>
                      <div className="mja-info-header__eyebrow">Partage</div>
                      <h5 className="mja-share-title mb-1">Partagé avec</h5>
                      <p className="mja-share-subtitle mb-0">Gère les accès à cette mesure pour les autres membres.</p>
                    </div>
                    <div className="mja-share-counter">
                      {annexedUsers.length} accès
                    </div>
                  </div>

                  {annexedUsers.length > 0 ? (
                    <div className="mja-share-current-list">
                      {annexedUsers.map(sharedUser => (
                        <div className="mja-share-chip-card" key={sharedUser.id}>
                          <div className="mja-share-chip-card__identity">
                            <img
                              src={getPhotoUrl(sharedUser.icon) || "images/client/office.png"}
                              className="img-fluid avatar avatar-ex-sm rounded"
                              alt="User"
                            />
                            <div className="mja-share-chip-card__text">
                              <strong>{sharedUser.name || sharedUser.email || 'N/A'}</strong>
                              <span>{sharedUser.email || 'Accès partagé'}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-link mja-share-remove"
                            onClick={() => this.removeUser(sharedUser)}
                          >
                            <Icon.X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mja-share-empty">
                      Aucun accès partagé pour le moment.
                    </div>
                  )}

                  <div className="mja-share-available">
                    <div className="mja-share-available__title">Partager avec un membre</div>
                    {fetchingUsers ? (
                      <div className="p-4 text-center">
                        <Spin />
                      </div>
                    ) : (
                      <div className="row">
                        {shareCandidates.map(userItem => (
                          <div className="col-lg-6 col-md-6 mt-3" key={userItem.id}>
                            <div className="mja-share-user-card">
                              <div className="mja-share-user-card__identity">
                                <img
                                  src={getPhotoUrl(userItem.icon) || "images/client/office.png"}
                                  className="img-fluid avatar avatar-large rounded shadow"
                                  alt="User"
                                />
                                <div className="mja-share-user-card__text">
                                  <h5 className="mb-1">
                                    {userItem.name || userItem.email || 'N/A'}
                                  </h5>
                                  <span>{userItem.email || 'Membre disponible'}</span>
                                </div>
                              </div>
                              <button
                                className="btn btn-sm mja-share-action"
                                onClick={() => this.addUser(userItem)}
                              >
                                <Icon.UserPlus size={16} className="mr-1" />
                                Partager
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {canUpdate && (
                <>
                  <div className="col-md-12 mt-4 pt-1">
                    <div className="form-group position-relative">
                      <label>Remarques</label>
                      <Icon.MessageCircle className="fea icon-sm icons" />
                      <textarea
                        name="comments"
                        id="comments_content"
                        rows={4}
                        className="form-control pl-5 mja-textarea"
                        placeholder="Votre remarque :"
                        defaultValue={selectedElement.bon?.client_comment || ""}
                      />
                    </div>
                  </div>
                  <div className="mt-4 pt-2">
                    <button
                      className="btn btn-block btn-primary mja-primary-cta"
                      onClick={this.updateMerchandise}
                      disabled={isLoading || hasDuplicates || hasIncompleteLine || round2(sumWeights) !== round2(net)}
                    >
                      {isLoading ? <Spin /> : 'Valider les marchandises'}
                    </button>
                  </div>
                </>
              )}

              {selectedElement.measure?.photos?.length > 0 && (
                <div className="col-md-12 mt-3">
                  <label className="h6 font-weight-bold">Photos à charge</label>
                  <PhotosComponent
                    photos={selectedElement.measure.photos.map(photo => ({
                      src: getPhotoUrl(photo),
                      width: 4,
                      height: 3
                    }))}
                  />
                </div>
              )}

              {selectedElement.measure_empty?.photos?.length > 0 && (
                <div className="col-md-12 mt-3">
                  <label className="h6 font-weight-bold">Photos à vide</label>
                  <PhotosComponent
                    photos={selectedElement.measure_empty.photos.map(photo => ({
                      src: getPhotoUrl(photo),
                      width: 4,
                      height: 3
                    }))}
                  />
                </div>
              )}

              {selectedElement.photos?.length > 0 && (
                <div className="col-md-12 mt-3">
                  <label className="h6 font-weight-bold">Autres</label>
                  <PhotosComponent
                    photos={selectedElement.photos.map(photo => ({
                      src: getPhotoUrl(photo),
                      width: 4,
                      height: 3
                    }))}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <Modal
          visible={onEditBills}
          onCancel={() => this.setState({ onEditBills: false })}
          cancelText="Annuler"
          width={1000}
          footer={null}
        >
          <BillsDivider
            identifier={selectedElement.id}
            weight={selectedElement.net_resulting_tonnage}
            onUpdated={() => {
              this.getMeasureElement();
              this.setState({
                onEditBills: false,
                canDivideBons: false
              });
              // Inform parent to refresh list while keeping this modal open
              if (this.props.onLiteUpdated) this.props.onLiteUpdated();
            }}
          />
        </Modal>
      </div>
    );
  }
}

export default MeasureDetails;
